import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Tab, Tabs } from '@vlabs/uikit';
import i18next from 'i18next';
import { generatePath, useHistory } from 'react-router-dom';

import st from './PageTabSelector.module.sass';
import type { PageTab } from './useActivePageTab';
import { useActivePageTab } from './useActivePageTab';
import { routes } from '../../../app/routes';

type PageTabsMap = Record<PageTab, string>;
const PAGE_TABS: PageTabsMap = {
  get cards() {
    return i18next.t('cards-and-lists:tabs.cards');
  },
  get lists() {
    return i18next.t('cards-and-lists:tabs.lists');
  },
};
const PAGE_TABS_ARRAY = Object.keys(PAGE_TABS) as PageTab[];

function PageTabSelector(): React.JSX.Element {
  const history = useHistory();
  const activeTab = useActivePageTab();
  const tabsWrapperRef = useRef<HTMLDivElement>(null);

  const activateTab = useCallback((tab: PageTab): void => {
    if (tab === activeTab) return;

    history.push(generatePath(routes.cardsAndLists.activeTab, { activeTab: tab }));
  }, [activeTab, history]);

  // Tabs из uikit вызывают onTabActivation на маунте для первого доступного таба,
  // поэтому для route tabs слушаем только реальные действия пользователя по хедерам
  const handleTabHeaderInteraction = useCallback((target: EventTarget | null): void => {
    const element = target as HTMLElement | null;
    const header = element?.closest('.Tabs__Header') as HTMLElement | null;

    if (!header) return;

    const index = Number(header.dataset.index);
    const tab = PAGE_TABS_ARRAY[index];

    if (!tab) return;

    activateTab(tab);
  }, [activateTab]);

  useEffect(() => {
    const node = tabsWrapperRef.current;

    if (!node) return undefined;

    // слушатели click/keyup вешаем на wrapper, потому что заголовки табов рендерятся
    // внутри uikit Tabs, а нам нужно реагировать только на действия пользователя
    const handleClick = (event: Event): void => {
      handleTabHeaderInteraction(event.target);
    };

    node.addEventListener('click', handleClick);
    node.addEventListener('keyup', handleClick);

    return () => {
      node.removeEventListener('click', handleClick);
      node.removeEventListener('keyup', handleClick);
    };
  }, [handleTabHeaderInteraction]);

  return (
    <div ref={tabsWrapperRef}>
      <Tabs
        activeTabIndex={PAGE_TABS_ARRAY.findIndex((t) => t === activeTab)}
        className={st.Tabs}
      >
        {(Object.entries(PAGE_TABS) as [PageTab, string][]).map(([tab, header]) => (
          <Tab
            header={header}
            key={tab}
            name={tab}
          />
        ))}
      </Tabs>
    </div>
  );
}

export { PageTabSelector };
